<template>
  <div>
    <a-form layout="inline">
      <a-form-item>
        <a-button v-permission="PERMISSIONS.CHANNEL_GROUP_MANAGE" @click="createChannelGroup" style="margin-right: 20px; margin-bottom: 10px"
          >创建通知通道组</a-button
        >
        <a-input placeholder="请输入通道组名称" v-model="form.channel_group_name" style="width: 200px; margin-right: 20px; margin-bottom: 10px"></a-input>
        <a-button type="primary" @click="search">搜索</a-button>
        <a-button @click="reset">重置</a-button>
      </a-form-item>
    </a-form>
    <a-table
      bordered
      :data-source="dataSource"
      :columns="columns"
      :pagination="false"
      :loading="loading"
      :rowKey="record => record.id"
      :expandedRowKeys="channelGroupState.expandedRowKeys"
      @expand="(expanded, record) => changeStateByChannelGroupId(record.id)"
    >
      <template slot="id" slot-scope="record">
        <div class="copy-cell">
          <span class="ellipsis-1" :title="record.id">{{ record.id }}</span>
          <a-icon type="copy" class="copy-icon" @click="$copy(record.id)" />
        </div>
      </template>
      <template slot="name" slot-scope="record">
        <div class="copy-cell">
          <span class="ellipsis-1" :title="record.name">{{ record.name }}</span>
          <a-icon type="copy" class="copy-icon" @click="$copy(record.name)" />
        </div>
      </template>
      <template slot="site" slot-scope="record">
        <div>
          {{ siteOptions[record.site_id] }}
        </div>
      </template>
      <template slot="channel_list" slot-scope="channel_list">
        <a-tag v-for="channel in channel_list" :key="channel.channel_id" color="blue">{{ channel.channel_name }}</a-tag>
      </template>
      <template slot="operation" slot-scope="record">
        <div class="button-box">
          <a-button @click="showChannelGroup(record)">展示</a-button>
          <a-button v-permission="PERMISSIONS.CHANNEL_GROUP_MANAGE" @click="updateChannelGroup(record)">编辑</a-button>
          <a-popconfirm title="确认删除这条消息?" ok-text="Yes" cancel-text="No" @confirm="deleteChannelGroup(record)">
            <a-button v-permission="PERMISSIONS.CHANNEL_GROUP_MANAGE">删除</a-button>
          </a-popconfirm>
        </div>
      </template>
      <template slot="expandedRowRender">
        <a-table
          :data-source="channelGroupRuleState.currentList"
          :columns="channelGroupRuleColumns"
          :row-key="it => it.id"
          size="small"
          bordered
          :pagination="false"
        >
          <template slot="ruleLevel" slot-scope="text, record">
            <template v-if="record.editing">
              <a-input v-model="record.todo.level" />
            </template>
            <template v-else>
              {{ text }}
            </template>
          </template>
          <template slot="ruleModule" slot-scope="text, record">
            <template v-if="record.editing">
              <a-input v-model="record.todo.module" />
            </template>
            <template v-else>
              {{ text }}
            </template>
          </template>
          <template slot="ruleSystem" slot-scope="text, record">
            <template v-if="record.editing">
              <a-input v-model="record.todo.system" />
            </template>
            <template v-else>
              {{ text }}
            </template>
          </template>
          <template slot="ruleOperationTitle">
            <a-button :disabled="!computedChannelGroupRuleEditable" @click="enterChannelGroupRuleCreatingMode()" size="small">创建订阅规则</a-button>
          </template>
          <template slot="ruleOperation" slot-scope="text, record">
            <template v-if="record.editing">
              <a-button size="small" @click="cancelChannelGroupRuleEditingMode(record)">取消</a-button>
              <a-button size="small" type="primary" @click="submitChannelGroupRuleSave(record)">保存</a-button>
            </template>
            <template v-else>
              <a-button size="small" type="primary" :disabled="!computedChannelGroupRuleEditable" @click="enterChannelGroupRuleEditingMode(record)"
                >编辑
              </a-button>
              <a-popconfirm style="margin-left: 10px" title="是否确认删除？" @confirm="submitChannelGroupRuleDelete(record)">
                <a-button size="small" type="danger" :disabled="!computedChannelGroupRuleEditable">删除</a-button>
              </a-popconfirm>
            </template>
          </template>
        </a-table>
      </template>
    </a-table>

    <div style="display: flex; justify-content: flex-end; margin-top: 10px">
      <a-pagination
        v-model="currentPage"
        :page-size-options="pageSizeOptions"
        :total="total"
        :page-size="pageSize"
        :showSizeChanger="true"
        :showQuickJumper="true"
        :show-total="total => `共 ${total} 条`"
        @change="pageChange"
        @showSizeChange="pageChange"
      />
    </div>
  </div>
</template>

<script>
import { deleteChannelGroup, getChannelGroupList } from '@/api/notice'
import * as channelGroupRuleApi from '@/api/channel/channel_group_rule'

// eslint-disable-next-line no-unused-vars
function __type__() {
  /**
   * @typedef {{pageSize: number, currentPage: number}} PageRequest
   */
  // eslint-disable-next-line no-unused-vars
  function __type__PageRequest() {}

  /**
   * @template T
   * @typedef {{pageSize: number, currentPage: number, totalSize: number, totalPage: number, currentList: T[]}} PageResponse
   */
  // eslint-disable-next-line no-unused-vars
  function __type__PageResponse() {}

  /**
   * @template T
   * @typedef {{code: number, message: string, data: T, traceid: string}} R
   */
  // eslint-disable-next-line no-unused-vars
  function __type__R() {}

  /**
   * @typedef {{
   *   id?: number
   *   createTime?: string
   *   upateTime?: string
   *   channelGroupId?: string
   *   channelGroupName?: string
   *   level?: string
   *   module?: string
   *   system?: string
   * }} ChannelGroupRuleDTO
   */
  // eslint-disable-next-line no-unused-vars
  function __type__ChannelGroupRuleDTO() {}

  /**
   * @typedef {Omit<ChannelGroupRuleDTO, 'id'|'createTime'|'updateTime'>} ChannelGroupFindQuery
   */
  // eslint-disable-next-line no-unused-vars
  function __type__ChannelGroupFindQuery() {}

  /**
   * @typedef {Omit<ChannelGroupRuleDTO, 'id'|'createTime'|'updateTime'|'channelGroupName'>} ChannelGroupRuleSaveReq
   */
  // eslint-disable-next-line no-unused-vars
  function __type__ChannelGroupRuleSaveReq() {}
}

export default {
  data() {
    return {
      // 【通道组】状态
      channelGroupState: {
        /**
         * 当前展开的通道组的编号
         * @type string[]
         */
        expandedRowKeys: []
      },
      // 【通道组订阅规则】状态
      channelGroupRuleState: {
        /**
         * @typedef {ChannelGroupRuleDTO & {editing: boolean, todo: ChannelGroupRuleDTO}} InlineEditChannelGroupRule
         * @type {InlineEditChannelGroupRule[]}
         */
        currentList: []
      },
      columns: [
        {
          title: 'id',
          key: 'id',
          width: 180,
          scopedSlots: { customRender: 'id' }
        },
        {
          title: '名称',
          key: 'name',
          width: 260,
          scopedSlots: { customRender: 'name' }
        },
        {
          title: '通道列表',
          dataIndex: 'channel_list',
          width: '30%',
          scopedSlots: { customRender: 'channel_list' }
        },
        {
          title: '创建时间',
          dataIndex: 'create_time',
          width: '15%'
        },
        {
          title: '更新时间',
          dataIndex: 'update_time',
          width: '15%'
        },
        {
          title: '操作',
          key: 'operation',
          scopedSlots: { customRender: 'operation' }
        }
      ],
      // 【通道组订阅规则】表格列
      channelGroupRuleColumns: [
        {
          title: '编号',
          dataIndex: 'id',
          key: 'id',
          width: 100
        },
        {
          title: '创建时间',
          dataIndex: 'createTime',
          key: 'createTime',
          width: 200
        },
        {
          title: '订阅规则-级别',
          dataIndex: 'level',
          key: 'level',
          width: 200,
          scopedSlots: { customRender: 'ruleLevel' }
        },
        {
          title: '订阅规则-模块',
          dataIndex: 'module',
          key: 'module',
          width: 200,
          scopedSlots: { customRender: 'ruleModule' }
        },
        {
          title: '订阅规则-系统',
          dataIndex: 'system',
          key: 'system',
          width: 200,
          scopedSlots: { customRender: 'ruleSystem' }
        },
        {
          key: 'ruleOperation',
          scopedSlots: { customRender: 'ruleOperation', title: 'ruleOperationTitle' }
        }
      ],
      form: {
        channel_group_name: '' // 通道组名称
      },
      dataSource: [],
      pageSizeOptions: ['20', '50', '100'],
      currentPage: 1,
      pageSize: 20,
      total: 1,
      loading: false,
      noticeVisible: false,
      content: '',
      siteOptions: {}
    }
  },
  created() {
    this.getChannelGroup()
  },
  methods: {
    getChannelGroup() {
      const params = {
        page_num: this.currentPage,
        page_size: this.pageSize
      }
      this.loading = true
      getChannelGroupList(params)
        .then(res => {
          const data = res.data
          // data.list.map(res=>{
          //   const li = JSON.stringify(res.channel_list)
          //   res.channel_list = li?li.length>0:''
          // })
          this.dataSource = data.list
          this.total = data.total
          this.loading = false
        })
        .catch(() => {
          this.dataSource = []
          this.total = 0
          this.loading = false
        })
    },
    search() {
      this.currentPage = 1
      this.pageSize = 20
      const params = {
        page_num: this.currentPage,
        page_size: this.pageSize,
        name: this.form.channel_group_name
      }
      this.loading = true
      getChannelGroupList(params)
        .then(res => {
          console.log(res)
          this.dataSource = res.data.list
          this.total = res.data.total
          this.loading = false
        })
        .catch(() => {
          this.dataSource = []
          this.total = 0
          this.loading = false
        })
    },
    reset() {
      this.form = {
        channel_group_name: '' // 通道组名称
      }
      this.pageChange(1, 20)
    },
    pageChange(page, pageSize) {
      this.currentPage = page
      this.pageSize = pageSize
      this.getChannelGroup()
    },
    createChannelGroup() {
      this.$router.push({ name: 'CreateChannelGroup', query: { type: 'create' } })
    },
    updateChannelGroup(data) {
      this.$router.push({ name: 'CreateChannelGroup', query: { type: 'update', id: data.id } })
    },
    deleteChannelGroup(data) {
      deleteChannelGroup(data.id).then(() => {
        this.$message.success('删除成功', 2)
        this.getChannelGroup()
      })
    },
    showChannelGroup(data) {
      this.$router.push({ name: 'CreateChannelGroup', query: { type: 'show', id: data.id } })
    },

    /**
     * 【通道组订阅规则】加载
     * @param {string} channelGroupId
     */
    async loadChannelGroupRule(channelGroupId) {
      if (channelGroupId) {
        const body = await channelGroupRuleApi.findChannelGroupRule({ channelGroupId, pageSize: Number.MAX_SAFE_INTEGER, currentPage: 1 })
        this.channelGroupRuleState.currentList = body.data.currentList.map(it => ({
          ...it,
          editing: false,
          todo: {}
        }))
      }
    },

    /**
     * 【通道组订阅规则】提交保存
     * @param {InlineEditChannelGroupRule} row
     */
    async submitChannelGroupRuleSave(row) {
      // 更新时读取 row.channelGroupId 而创建时读取 row.todo.channelGroupId
      const channelGroupId = row.channelGroupId || row.todo.channelGroupId
      await channelGroupRuleApi.saveChannelGroupRule(row.todo)
      await this.loadChannelGroupRule(channelGroupId)
    },

    /**
     * 【通道组订阅规则】提交删除
     * @param {InlineEditChannelGroupRule} row
     */
    async submitChannelGroupRuleDelete(row) {
      if (row.id) {
        await channelGroupRuleApi.deleteChannelGroupRule({ idList: [row.id] })
        await this.loadChannelGroupRule(row.channelGroupId)
      }
    },

    /**
     * 【通道组订阅规则】通过通道组编号修改状态
     * @param {string} channelGroupId
     */
    async changeStateByChannelGroupId(channelGroupId) {
      const shouldClose = this.channelGroupState.expandedRowKeys.length === 1 && this.channelGroupState.expandedRowKeys[0] === channelGroupId
      if (shouldClose) {
        this.channelGroupState.expandedRowKeys = []
        this.channelGroupRuleState.currentList = []
      } else {
        this.channelGroupState.expandedRowKeys = [channelGroupId]
        await this.loadChannelGroupRule(channelGroupId)
      }
    },

    /**
     * 【通道组订阅规则】进入创建模式
     */
    async enterChannelGroupRuleCreatingMode() {
      if (this.channelGroupState.expandedRowKeys.length === 1) {
        this.channelGroupRuleState.currentList.splice(0, 0, {
          editing: true,
          todo: {
            channelGroupId: this.channelGroupState.expandedRowKeys[0],
            level: '*',
            module: '*',
            system: '*'
          }
        })
        console.log(this.channelGroupRuleState.currentList)
      }
    },

    /**
     * 【通道组订阅规则】进入编辑模式
     * @param {InlineEditChannelGroupRule} row
     */
    async enterChannelGroupRuleEditingMode(row) {
      row.editing = true
      row.todo = {
        id: row.id,
        channelGroupId: row.channelGroupId,
        level: row.level || '*',
        module: row.module || '*',
        system: row.system || '*'
      }
    },

    /**
     * 【通道组订阅规则】退出编辑模式
     * @param {InlineEditChannelGroupRule} row
     */
    async cancelChannelGroupRuleEditingMode(row) {
      row.editing = false
      row.todo = {}
      if (!row.id) {
        this.channelGroupRuleState.currentList.splice(0, 1)
      }
    }
  },
  computed: {
    /**
     * 【通道组订阅规则】计算是否可进入行内编辑模式
     * @returns {boolean}
     */
    computedChannelGroupRuleEditable() {
      return this.channelGroupRuleState.currentList.findIndex(e => e.editing) === -1
    }
  }
}
</script>

<style scoped>
.button-box {
  display: flex;
  justify-content: space-evenly;
}

::v-deep .ant-table {
  height: calc(100vh - 220px);
  overflow: auto scroll;
}

::v-deep .ant-table .ant-table-thead {
  position: sticky;
  top: 0;
  background-color: #fff;
  /* 设置表头背景颜色 */
  z-index: 10;
  /* 设置表头层级 */
}

.ellipsis-1 {
  max-width: none;
}
</style>
